import { LoadingOverlay, PasswordInput } from '_components/atoms';
import { useAppDispatch } from '_hooks';
import { HelperMethods } from '_utils';
import { signIn } from 'aws-amplify/auth';

import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  // redux functions
  const dispatch = useAppDispatch();

  // Extract returnTo path from location state if it exists
  const returnTo = location.state?.returnTo || '/';

  // Check if form is valid
  const isFormValid = useMemo(() => {
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);
    const isValidPassword = password.length > 0;
    return isValidEmail && isValidPassword;
  }, [username, password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setError('');

    try {
      await signIn({
        username,
        password,
      });

      HelperMethods.checkUserSession(dispatch);
      // Navigate to the return path or home page after successful sign in
      navigate(returnTo, { replace: true });
    } catch (error: any) {
      switch (error?.name) {
        case 'NotAuthorizedException':
          setError('Invalid credentials');
          break;
        case 'UserNotFoundException':
          setError('User not found');
          break;
        case 'UserNotConfirmedException':
          setError('User not confirmed');
          break;
        default:
          setError(error.message || 'Failed to sign in');
      }
      console.error('Sign in error:', JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Loading overlay for authentication process */}
      <LoadingOverlay isLoading={loading} message={'Signing you in...'} />

      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="text-center text-xl font-bold text-gray-900">
            {'Sign In'}
          </h2>
        </div>

        {/* <div>
          <GoogleButton returnTo={returnTo} />
        </div> */}

        {/* <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-gray-50 text-gray-500">
              {'Or continue with'}
            </span>
          </div>
        </div> */}

        <form className="space-y-6" onSubmit={handleSubmit}>
          {error?.length > 0 && (
            <div className="text-red-500 text-center text-sm">{error}</div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                type="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={'Email'}
                value={username}
                onChange={e => {
                  setUsername(e.target.value);
                  setError('');
                }}
              />
            </div>
            <div>
              <PasswordInput
                id="password"
                name="password"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder={'Password'}
                className="rounded-b-md"
                autoComplete="current-password"
              />
            </div>
          </div>
          <div className="flex flex-col space-y-3">
            <button
              type="submit"
              disabled={loading || !isFormValid}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                isFormValid
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-400 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Loading...' : 'Sign In'}
            </button>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                {'Forgot Password'}
              </button>
            </div>
          </div>
        </form>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            {'No account?'}{' '}
            <button
              onClick={() => navigate('/sign-up', { state: { returnTo } })}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              {'Create Account'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
